/**
 * Layout metrics computed from the document's own settings + styles. Every px
 * value derives from the file's 0.1mm units via unitsToPx — never hardcoded —
 * so the editor matches Fade In's page geometry for any document.
 */
import type { Align, DocSettings, StyleDef } from "../format/types";
import { resolveStyle, unitsToPx } from "../format/styles";

export interface StyleMetrics {
  name: string;
  /** Left indent from the content margin (px). */
  marginLeftPx: number;
  /** Right indent from the content margin (px). */
  marginRightPx: number;
  /** Space above the paragraph (px), from spacebefore lines. */
  spaceBeforePx: number;
  align: Align;
  allcaps: boolean;
}

export interface PageMetrics {
  pageWidthPx: number;
  pageHeightPx: number;
  marginTopPx: number;
  marginBottomPx: number;
  marginLeftPx: number;
  marginRightPx: number;
  contentWidthPx: number;
  fontSizePt: number;
  lineHeightPx: number;
  styles: Map<string, StyleMetrics>;
}

export function computeMetrics(
  settings: DocSettings,
  styles: Map<string, StyleDef>,
): PageMetrics {
  const marginLeftPx = unitsToPx(settings.marginLeft);
  const marginRightPx = unitsToPx(settings.marginRight);
  const pageWidthPx = unitsToPx(settings.pageWidth);
  // 6 lines per inch → each line is 96/6 = 16px at 96dpi.
  const lineHeightPx = 96 / settings.linesPerInch;

  const styleMetrics = new Map<string, StyleMetrics>();
  for (const name of styles.keys()) {
    const r = resolveStyle(styles, name);
    styleMetrics.set(name, {
      name,
      marginLeftPx: unitsToPx(r.leftindent),
      marginRightPx: unitsToPx(r.rightindent),
      spaceBeforePx: r.spacebefore * lineHeightPx,
      align: r.align,
      allcaps: r.allcaps,
    });
  }

  return {
    pageWidthPx,
    pageHeightPx: unitsToPx(settings.pageHeight),
    marginTopPx: unitsToPx(settings.marginTop),
    marginBottomPx: unitsToPx(settings.marginBottom),
    marginLeftPx,
    marginRightPx,
    contentWidthPx: pageWidthPx - marginLeftPx - marginRightPx,
    fontSizePt: 12,
    lineHeightPx,
    styles: styleMetrics,
  };
}

/** CSS-escape a style name for use in an attribute selector. */
function cssAttr(value: string): string {
  return value.replace(/["\\]/g, "\\$&");
}

/**
 * Generate a scoped stylesheet from the metrics. `scope` is a class selector
 * (e.g. ".sp-page") applied to the page container; the ProseMirror surface and
 * paragraphs live inside it.
 */
export function metricsCss(m: PageMetrics, scope: string): string {
  const lines: string[] = [];
  const px = (n: number) => `${n.toFixed(3)}px`;

  lines.push(`${scope} {
  width: ${px(m.pageWidthPx)};
  min-height: ${px(m.pageHeightPx)};
  padding: ${px(m.marginTopPx)} ${px(m.marginRightPx)} ${px(m.marginBottomPx)} ${px(m.marginLeftPx)};
  box-sizing: border-box;
  background: var(--page-bg, #fff);
  color: #111;
}`);

  lines.push(`${scope} .ProseMirror {
  font-family: "Courier Prime", "Courier New", monospace;
  font-size: 12pt;
  line-height: ${px(m.lineHeightPx)};
  outline: none;
  min-height: 100%;
}`);

  lines.push(`${scope} .para {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}`);

  for (const s of m.styles.values()) {
    const sel = `${scope} .para[data-style="${cssAttr(s.name)}"]`;
    const decl: string[] = [
      `margin-left: ${px(s.marginLeftPx)}`,
      `margin-right: ${px(s.marginRightPx)}`,
      `margin-top: ${px(s.spaceBeforePx)}`,
      `text-align: ${s.align}`,
    ];
    if (s.allcaps) decl.push("text-transform: uppercase");
    lines.push(`${sel} { ${decl.join("; ")}; }`);
  }

  // Paragraph-level alignment overrides (from <style align="...">).
  lines.push(`${scope} .para[data-align="center"] { text-align: center; }`);
  lines.push(`${scope} .para[data-align="left"] { text-align: left; }`);
  lines.push(`${scope} .para[data-align="right"] { text-align: right; }`);

  return lines.join("\n");
}
